import { useSyncExternalStore } from 'react';
import type { Lang } from '@/data/types';

/**
 * Editable portal copy — a curated set of high-visibility texts that super
 * admins can rewrite per language from Portal management (app_settings,
 * scope copy), without a deploy. Everything not in this registry stays in code.
 */
export interface CopyDef { label: string; en: string; es: string; fr: string; pt: string }

export const COPY_DEFAULTS: Record<string, CopyDef> = {
  'qa.applications': {
    label: 'Quick Access · Applications (título)',
    en: 'Applications', es: 'Aplicaciones', fr: 'Applications', pt: 'Aplicações',
  },
  'qa.jurisdiction': {
    label: 'Quick Access · By jurisdiction (título)',
    en: 'By jurisdiction', es: 'Por jurisdicción', fr: 'Par juridiction', pt: 'Por jurisdição',
  },
  'qa.general': {
    label: 'Quick Access · General access (título)',
    en: 'General access', es: 'Generales', fr: 'Généraux', pt: 'Gerais',
  },
  'qa.search': {
    label: 'Quick Access · placeholder del buscador',
    en: 'Find tools, shortcuts and skills…',
    es: 'Buscar herramientas, accesos y skills…',
    fr: 'Rechercher outils, accès et skills…',
    pt: 'Procurar ferramentas, acessos e skills…',
  },
  'mw.workspace': {
    label: 'My Work · cabecera del espacio',
    en: 'My Workspace', es: 'Mi espacio', fr: 'Mon espace', pt: 'Meu espaço',
  },
  'admin.title': {
    label: 'Admin · título',
    /* Matches the sidebar door word for word — one name, outside and inside. */
    en: 'Admin panel', es: 'Admin panel', fr: 'Admin panel', pt: 'Admin panel',
  },
  /* The four kanban columns — renaming one renames it on every board and on
     the stages panel, for everyone, instantly. Departments with their own
     vocabulary (Tax, Real Estate, Recurring) keep it. */
  'stage.docs': {
    label: 'Kanban · columna 1',
    en: 'Initial formalities', es: 'Formalidades previas', fr: 'Formalités initiales', pt: 'Formalidades iniciais',
  },
  'stage.review': {
    label: 'Kanban · columna 2',
    en: 'Docs review', es: 'Docs review', fr: 'Revue des docs', pt: 'Revisão de docs',
  },
  'stage.final': {
    label: 'Kanban · columna 3',
    en: 'Final check', es: 'Chequeo final', fr: 'Contrôle final', pt: 'Verificação final',
  },
  'stage.waiting': {
    label: 'Kanban · columna 4',
    en: 'Waiting resolution', es: 'Esperando resolución', fr: 'Attente de résolution', pt: 'Aguardando resolução',
  },
  'admin.subtitle': {
    label: 'Admin · subtítulo',
    en: 'Manage portal accounts, credentials and the Case Intelligence dataset.',
    es: 'Gestiona cuentas del portal, credenciales y el dataset de Case Intelligence.',
    fr: 'Gérez les comptes du portail, les identifiants et le jeu de données Case Intelligence.',
    pt: 'Gerencie contas do portal, credenciais e o dataset de Case Intelligence.',
  },
};

export interface CopyOverride { key: string; en: string | null; es: string | null; fr: string | null; pt: string | null }

let MAP = new Map<string, CopyOverride>();
let version = 0;
const listeners = new Set<() => void>();

export function setCopyOverrides(list: CopyOverride[]) {
  MAP = new Map(list.map((o) => [o.key, o]));
  version++;
  listeners.forEach((l) => l());
}
export function copyOverrideFor(key: string): CopyOverride | undefined {
  return MAP.get(key);
}

/** Effective text for a copy key in a language (override → default → key). */
export function copyText(key: string, lang: Lang): string {
  const o = MAP.get(key);
  const d = COPY_DEFAULTS[key];
  return (o?.[lang] || undefined) ?? (d ? d[lang] : key);
}

/** Reactive accessor — re-renders the caller when admin overrides arrive. */
export function useCopy(lang: Lang): (key: string) => string {
  useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => version,
    () => version,
  );
  return (key: string) => copyText(key, lang);
}
